import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  Home, Users, BookOpen, Calendar, FileText, Tv, Handshake,
  Phone, Mail, Link as LinkIcon, ChevronLeft, ChevronRight,
  Factory, Award, Building2, TrendingUp, Globe, Building,
  Truck, Train, Plane, Anchor, Gem, Wheat, TreePine, Droplets,
  ShoppingBag,
  Ship,
  Info,
  Zap,
  Flame,
  AlertCircle,
  GraduationCap,
  Download,
  Newspaper,
  Trophy,
  ArrowRight,
  Menu,
  X
} from "lucide-react";
import '../industry/industry.css';
import { Card, Col, Row, Typography, Space, Tag, ConfigProvider, Button, Divider } from 'antd';
import {
  ThunderboltOutlined,
  DashboardOutlined,
  FireOutlined,
  SafetyCertificateOutlined,
  BankOutlined,
  SolutionOutlined,
  RocketOutlined,
  UsergroupAddOutlined,
  ReadOutlined,
  FileProtectOutlined,
  InfoCircleOutlined,
  CalendarOutlined,
  LeftOutlined,
  RightOutlined,
  BuildOutlined,
  ApartmentOutlined
} from '@ant-design/icons';
const { Title, Text, Paragraph } = Typography;


const DistrictDetails = () => {
  const { id } = useParams();
  const containerRef = useRef(null);

  const [district, setDistrict] = useState(null);
  // console.log('DistrictPage district state:', district);
  // Add new state for companies
  const [companies, setCompanies] = useState([]);
  // console.log('DistrictPage companies state:', companies);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [companiesError, setCompaniesError] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const brandPurple = "#3C007A";
  const brandOrange = "#FF8F00";
  const textDark = "#333333";
  const textMuted = "#666666";

  const scroll = (direction) => {
    if (containerRef.current) {
      const scrollAmount = direction === 'left' ? -400 : 400;
      containerRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };
  const hasOpportunities = Array.isArray(district?.investmentOpportunities) && district?.investmentOpportunities.length > 0;

  const containerStyle = {
    fontFamily: "'Rubik', sans-serif",
    color: textDark,
    backgroundColor: "#F8F9FA",
    padding: "20px"
  };

  const sectionHeaderStyle = {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "32px",
    borderBottom: `2px solid #E5E7EB`,
    paddingBottom: "12px"
  };

  const accentBarStyle = {
    width: "6px",
    height: "28px",
    backgroundColor: brandOrange,
    borderRadius: "4px"
  };

  const statCardStyle = {
    position: "relative",
    background: "#fff",
    borderRadius: "16px",
    padding: "45px 20px 25px",
    textAlign: "center",
    boxShadow: "0 10px 30px rgba(60, 0, 122, 0.04)",
    border: "1px solid #F0F0F0",
    transition: "all 0.3s ease"
  };

  const iconCircleStyle = {
    position: "absolute",
    top: "-30px",
    left: "50%",
    transform: "translateX(-50%)",
    width: "64px",
    height: "64px",
    backgroundColor: brandPurple,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: `0 8px 16px rgba(60, 0, 122, 0.2)`,
    color: "#fff",
    border: "4px solid #fff"
  };

  const connectivityCardStyle = {
    display: "flex",
    alignItems: "flex-start",
    gap: "16px",
    padding: "20px",
    background: "#fff",
    borderRadius: "12px",
    borderLeft: `4px solid ${brandPurple}`,
    boxShadow: "0 4px 12px rgba(0,0,0,0.03)"
  };


  useEffect(() => {
    const fetchDistrictData = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/districts/id/${id}`);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        if (result.success) {
          setDistrict(result.data);
        } else {
          throw new Error(result.message || 'Failed to fetch district data');
        }
      } catch (err) {
        setError(err.message);
        console.error('Error fetching district:', err);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchDistrictData();
    }
  }, [id]);

  // New useEffect to fetch companies when district data is loaded
  useEffect(() => {
    const fetchDistrictCompanies = async () => {
      if (!district?.districtName) return;

      try {
        setCompaniesLoading(true);
        setCompaniesError(null);

        // Fetch companies filtered by district name (city)
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/companies/bycity?city=${encodeURIComponent(district.districtName)}`
        );

        // console.log('Fetching companies for district:', response.data);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        if (result.success) {
          setCompanies(result.data || []);
        } else {
          throw new Error(result.message || 'Failed to fetch companies');
        }
      } catch (err) {
        setCompaniesError(err.message);
        console.error('Error fetching companies:', err);
      } finally {
        setCompaniesLoading(false);
      }
    };

    fetchDistrictCompanies();
  }, [district?.districtName]);

  // UI state for section-wise tabs
  const [activeSection, setActiveSection] = useState('overview');
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // Sidebar items config
  const sidebarItems = [
    { key: 'overview', label: 'Overview', icon: <Home size={19} /> },
    { key: 'chair', label: 'Conveners/Co-Conveners', icon: <Users size={19} /> },
    { key: 'industrial', label: 'Industrial Classification', icon: <BookOpen size={19} /> },
    { key: 'resources', label: 'Natural Resources', icon: <Calendar size={19} /> },
    { key: 'incentives', label: 'Industrial Incentives & Benefits', icon: <FileText size={19} /> },
    { key: 'opportunities', label: 'Investment Opportunities', icon: <Handshake size={19} /> },
    { key: 'policies', label: 'Policies', icon: <FileText size={19} /> },
    { key: 'schemes', label: 'Schemes', icon: <BookOpen size={19} /> },
    { key: 'press', label: 'Press Releases', icon: <FileText size={19} /> },
    { key: 'media', label: 'Media Coverage', icon: <Tv size={19} /> },
    { key: 'awards', label: 'Awards & Recognition', icon: <Users size={19} /> },
    { key: 'downloads', label: 'Resources', icon: <FileText size={19} /> },
  ];

  // Small card component
  const Card = ({ title, children }) => (
    <div style={{
      background: "#fff",
      border: "1px solid #eee",
      borderRadius: 10,
      padding: 24,
      boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
      marginBottom: 24
    }}>
      <h4 style={{ margin: 0, color: "#333", marginBottom: 14 }}>{title}</h4>
      {children}
    </div>
  );

  // DetailGrid (removed explicit fontSize; kept color/weight)
  const DetailGrid = ({ items }) => (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16, marginTop: 8 }}>
      {items.map((it, i) => (
        <div key={i}>
          <div style={{ color: "#555", fontWeight: 400, fontSize: "16px" }}>{it.label}</div>
          <div style={{ color: "#222", fontWeight: 600, fontSize: "17px" }}>{it.value ?? "—"}</div>
        </div>
      ))}
    </div>
  );

  // Add slider state for opportunities
  const [opportunitiesScrollPosition, setOpportunitiesScrollPosition] = useState(0);
  const opportunitiesContainerRef = useRef(null);

  // Scroll handler for opportunities slider
  const scrollOpportunities = (direction) => {
    const container = opportunitiesContainerRef.current;
    if (!container) return;
    const scrollAmount = 380; // Card width + gap
    const newPosition = direction === 'left'
      ? Math.max(0, opportunitiesScrollPosition - scrollAmount)
      : Math.min(container.scrollWidth - container.clientWidth, opportunitiesScrollPosition + scrollAmount);

    container.scrollTo({ left: newPosition, behavior: 'smooth' });
    setOpportunitiesScrollPosition(newPosition);
  };

  // Add slider state for policies
  const [policiesScrollPosition, setPoliciesScrollPosition] = useState(0);
  const policiesContainerRef = useRef(null);

  // Add slider state for connectivity
  const [connectivityScrollPosition, setConnectivityScrollPosition] = useState(0);
  const connectivityContainerRef = useRef(null);

  // Scroll handler for policies slider
  const scrollPolicies = (direction) => {
    const container = policiesContainerRef.current;
    if (!container) return;
    const scrollAmount = 380;
    const newPosition = direction === 'left'
      ? Math.max(0, policiesScrollPosition - scrollAmount)
      : Math.min(container.scrollWidth - container.clientWidth, policiesScrollPosition + scrollAmount);

    container.scrollTo({ left: newPosition, behavior: 'smooth' });
    setPoliciesScrollPosition(newPosition);
  };

  // Scroll handler for connectivity slider
  const scrollConnectivity = (direction) => {
    const container = connectivityContainerRef.current;
    if (!container) return;
    const scrollAmount = 350;
    const newPosition = direction === 'left'
      ? Math.max(0, connectivityScrollPosition - scrollAmount)
      : Math.min(container.scrollWidth - container.clientWidth, connectivityScrollPosition + scrollAmount);

    container.scrollTo({ left: newPosition, behavior: 'smooth' });
    setConnectivityScrollPosition(newPosition);
  };

  // Add slider state for schemes
  const [schemesScrollPosition, setSchemesScrollPosition] = useState(0);
  const schemesContainerRef = useRef(null);

  // Scroll handler for schemes slider
  const scrollSchemes = (direction) => {
    const container = schemesContainerRef.current;
    if (!container) return;
    const scrollAmount = 380;
    const newPosition = direction === 'left'
      ? Math.max(0, schemesScrollPosition - scrollAmount)
      : Math.min(container.scrollWidth - container.clientWidth, schemesScrollPosition + scrollAmount);

    container.scrollTo({ left: newPosition, behavior: 'smooth' });
    setSchemesScrollPosition(newPosition);
  };

  // Add slider state for press releases
  const [pressScrollPosition, setPressScrollPosition] = useState(0);
  const pressContainerRef = useRef(null);

  // Scroll handler for press releases slider
  const scrollPress = (direction) => {
    const container = pressContainerRef.current;
    if (!container) return;
    const scrollAmount = 380;
    const newPosition = direction === 'left'
      ? Math.max(0, pressScrollPosition - scrollAmount)
      : Math.min(container.scrollWidth - container.clientWidth, pressScrollPosition + scrollAmount);

    container.scrollTo({ left: newPosition, behavior: 'smooth' });
    setPressScrollPosition(newPosition);
  };
  const mainStats = [
    {
      label: "Skilled Labor Availability",
      value: district?.skilledLabor,
      icon: <UsergroupAddOutlined />,
      gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      tags: ["Technical", "Vocational"]
    },
    {
      label: "Education Hubs",
      value: district?.educationDetails,
      icon: <ReadOutlined />,
      gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
      tags: ["Universities", "Engineering"]
    }
  ];

  // Specific counts derived from the screenshot style
  const detailedStats = [
    { count: district?.universitiesCount || "67", label: "Universities", icon: <BankOutlined style={{ color: '#3C007A' }} /> },
    { count: district?.collegesCount || "5842", label: "Colleges", icon: <BookOpen style={{ color: '#FF8F00' }} /> },
    { count: district?.itiCount || "3268", label: "ITIs / ITCs", icon: <SolutionOutlined style={{ color: '#3C007A' }} /> },
    { count: district?.polytechnicCount || "370", label: "Polytechnics", icon: <RocketOutlined style={{ color: '#FF8F00' }} /> }
  ];
  const infrastructureData = [
    { label: "Power Availability", value: district?.powerAvailability, icon: <ThunderboltOutlined />, color: "#333333" },
    { label: "Water Supply", value: district?.waterDetails, icon: <DashboardOutlined />, color: "#333333" },
    { label: "Gas Infrastructure (PNG)", value: district?.gasInfrastructure, icon: <FireOutlined />, color: "#333333" },
    { label: "Fire Safety Profile", value: district?.fireSafetyProfile, icon: <SafetyCertificateOutlined />, color: "#333333" }
  ];
  const ResourceHoverCard = ({ title, content, icon: Icon, iconColor, bgColor, imageSrc }) => {
    return (
      <div className="resource-flip-container">
        <div className="resource-flip-inner">

          {/* FRONT SIDE: Category & Icon */}
          <div className="resource-flip-front">
            <div className="icon-wrapper" >
              {imageSrc ? (
                <img
                  src={imageSrc}
                  alt={title}
                  style={{
                    width: '70px',
                    height: '70px',
                    objectFit: 'contain'
                  }}
                />
              ) : (
                <Icon size={32} color={iconColor} />
              )}
            </div>
            <Text className="resource-title">{title}</Text>
          </div>

          {/* BACK SIDE: Specific Resource Data */}
          <div className="resource-flip-back">
            <Title level={3} className="back-title">{title} Details</Title>
            <Text className="back-content">{content}</Text>
          </div>

        </div>

        <style jsx>{`
        .resource-flip-container {
          perspective: 1000px;
          height: 220px;
          cursor: pointer;
          font-family: 'Rubik', sans-serif;
        }

        .resource-flip-inner {
          position: relative;
          width: 100%;
          height: 100%;
          transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
          transform-style: preserve-3d;
        }

        .resource-flip-container:hover .resource-flip-inner {
          transform: rotateY(180deg);
        }

        .resource-flip-front, .resource-flip-back {
          position: absolute;
          width: 100%;
          height: 100%;
          backface-visibility: hidden;
          border-radius: 16px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 24px;
          box-shadow: 0 8px 20px rgba(0,0,0,0.06);
          border: 1px solid #eee;
        }

        .resource-flip-front {
          background: #fff;
        }

        .resource-flip-back {
          background: #fdfdfd;
          transform: rotateY(180deg);
          border-top: 4px solid #FF8F00;
          text-align: center;
        }

        .icon-wrapper {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 16px;
        }

        .resource-title {
          color: #3C007A;
          font-size: 18px;
          font-weight: 400;
        }

        .accent-bar {
          margin-top: 12px;
          height: 3px;
          width: 40px;
          background-color: #FF8F00;
          border-radius: 2px;
        }

        .back-title {
          color: #3C007A !important;
          font-size: 21px !important;
          margin-bottom: 12px !important;
          font-weight: 400 !important;
        }

        .back-content {
          color: #333333;
          font-size: 16px;
          font-weight: 400;
          line-height: 1.6;
        }
      `}</style>
      </div>
    );
  };
  const resources = [
    {
      title: "Minerals",
      content: district?.minerals,
      icon: Gem,
      iconColor: "#10b981",
      bgColor: "#e8f5e9",
      imageSrc: "/img/geological.png"
    },
    {
      title: "Agriculture",
      content: district?.agricultureProducts,
      icon: Wheat,
      iconColor: "#FF8F00",
      bgColor: "#fff3e0",
      imageSrc: "/img/agri.png"
    },
    {
      title: "Forestry",
      content: district?.forestResources,
      icon: TreePine,
      iconColor: "#059669",
      bgColor: "#ecfdf5",
      imageSrc: "/img/forest.png"
    },
    {
      title: "Water",
      content: district?.waterResources,
      icon: Droplets,
      iconColor: "#3C007A",
      bgColor: "#eef2ff",
      imageSrc: "/img/waterdrop.png"
    },
  ];

  const incentives = [
    {
      title: "Land Subsidy",
      available: district?.landSubsidy,
      icon: <BankOutlined />,
      desc: district?.landSubsidy ? "Eligible businesses can receive land subsidies" : "Land subsidy not currently offered"
    },
    {
      title: "Stamp Duty Exemption",
      available: district?.stampDutyExemption,
      icon: <FileProtectOutlined />,
      desc: district?.stampDutyExemption ? "Stamp duty exemption available for property registration" : "Standard stamp duty applies"
    },
    {
      title: "Power Subsidy",
      available: district?.powerSubsidy,
      icon: <ThunderboltOutlined />,
      desc: district?.powerSubsidy ? "Subsidized power tariffs for industrial units" : "Standard power tariffs apply"
    },
    {
      title: "Tax Holiday",
      available: district?.taxHoliday,
      icon: <SafetyCertificateOutlined />,
      desc: district?.taxHoliday ? "Tax exemption period for new industrial units" : "Standard tax regulations apply"
    }
  ];
  // Render section content
  const renderSection = () => {
    switch (activeSection) {
      case 'overview':
        return (
          <div style={containerStyle}>
            {/* About Section */}
            <section style={{ marginBottom: "40px" }}>
              <div style={sectionHeaderStyle}>
                {/* <div style={accentBarStyle} /> */}
                <h2 style={{ fontSize: "24px", fontWeight: 400, margin: 0, color: brandPurple }}>About District</h2>
              </div>
              <div style={{
                background: "#fff",
                padding: "32px",
                borderRadius: "16px",
                boxShadow: "0 4px 20px rgba(0,0,0,0.02)",
                lineHeight: "1.8",
                color: textMuted,
                fontSize: "17px"
              }}>
                {district.aboutDistrict || "Information regarding this district is currently being updated."}
              </div>
            </section>

            {/* Stats Grid */}
            <section style={{ marginBottom: "60px" }}>
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: "30px", // Increased gap slightly for better breathing room
                marginBottom: 24,
                paddingTop: "40px" // Extra space so the larger icons don't hit the top
              }}>
                {[
                  { label: "Industrial Zone", value: district.industrialZone, icon: <Factory size={32} strokeWidth={1.5} />, color: "#FF8F00" },
                  { label: "ODOP Product", value: district.odopProduct, icon: <ShoppingBag size={32} strokeWidth={1.5} />, color: "#FF8F00" },
                  { label: "MSME Count", value: district.msmeCount, icon: <Building2 size={32} strokeWidth={1.5} />, color: "#FF8F00" },
                  { label: "Large Industries", value: district.largeIndustriesCount, icon: <Ship size={32} strokeWidth={1.5} />, color: "#FF8F00" },
                  { label: "Growth Rate", value: district.industrialGrowthRate ? `${district.industrialGrowthRate}%` : null, icon: <TrendingUp size={32} strokeWidth={1.5} />, color: "#FF8F00" },
                  { label: "Website", value: district.districtWebsite ? "Visit Official Site" : null, icon: <Globe size={32} strokeWidth={1.5} />, color: "#FF8F00", isLink: true }
                ].map((stat, idx) => (
                  <div key={idx} style={{
                    position: "relative",
                    padding: "50px 20px 30px 20px", // Increased top padding for larger icons
                    background: "#fff",
                    borderRadius: "12px", // Slightly more rounded for refinement
                    boxShadow: "0 10px 25px rgba(0,0,0,0.05)",
                    borderTop: `6px solid ${stat.color}`,
                    textAlign: "center",
                    transition: "all 0.3s ease",
                    cursor: "pointer"
                  }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-8px)";
                      e.currentTarget.style.boxShadow = "0 15px 35px rgba(0,0,0,0.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "0 10px 25px rgba(0,0,0,0.05)";
                    }}>

                    {/* Larger Refined Icon Overlay */}
                    <div style={{
                      position: "absolute",
                      top: "-35px", // Adjusted for larger size
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: "70px", // Increased from 50px
                      height: "70px", // Increased from 50px
                      backgroundColor: "#fff",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                      border: "2px solid #f0f0f0", // Defined border for "refinement"
                      zIndex: 2,
                      color: "#444" // Darker icon color for better contrast
                    }}>
                      {stat.icon}
                    </div>

                    <div style={{
                      fontSize: "17px",
                      fontWeight: "400",
                      color: "#3C007A", // Darker orange for better legibility
                      marginBottom: "16px",
                      letterSpacing: "-0.5px"
                    }}>
                      {stat.value || "—"}
                    </div>

                    <div style={{
                      fontSize: "17px",
                      color: "#666666",
                      lineHeight: "1.4",
                      fontWeight: "400",
                      maxWidth: "90%",
                      margin: "0 auto"
                    }}>
                      {stat.isLink && stat.value ? (
                        <a href={district.districtWebsite} target="_blank" rel="noreferrer"
                          style={{ color: "#3C007A", textDecoration: "none", fontWeight: "600" }}>
                          {stat.label} ↗
                        </a>
                      ) : stat.label}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Major Industries Overview */}
            {district.majorIndustries && (
              <div style={{
                background: `linear-gradient(90deg, rgba(60,0,122,0.10) 0%, rgba(60,0,122,0.22) 100%)`,
                borderRadius: "16px",
                padding: "32px",
                color: "#3C007A",
                marginBottom: "40px",
                position: "relative",
                overflow: "hidden"
              }}>
                <div style={{ position: "relative", zIndex: 2 }}>
                  <h3 style={{ display: "flex", alignItems: "center", gap: "10px", margin: "0 0 16px 0", fontSize: "22px", color: textDark }}>
                    <Building size={24} /> Major Industries Overview
                  </h3>
                  <p style={{ margin: 0, opacity: 0.9, lineHeight: 1.7, fontSize: "17px" }}>{district.majorIndustries}</p>
                </div>
                <Factory size={120} style={{ position: "absolute", right: "-20px", bottom: "-20px", opacity: 0.1 }} />
              </div>
            )}

            {/* Connectivity Section */}
            <section style={{ marginBottom: "40px" }}>
              <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "32px",
                borderBottom: `2px solid #E5E7EB`,
                paddingBottom: "12px"
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  {/* <div style={accentBarStyle} /> */}
                  <h2 style={{ fontSize: "24px", fontWeight: 400, margin: 0, color: brandPurple }}>
                    Logistics & Connectivity
                  </h2>
                </div>

                {/* Navigation Buttons */}
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    onClick={() => scrollConnectivity('left')}
                    disabled={connectivityScrollPosition === 0}
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      border: "2px solid #E5E7EB",
                      background: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: connectivityScrollPosition === 0 ? "not-allowed" : "pointer",
                      opacity: connectivityScrollPosition === 0 ? 0.4 : 1,
                      transition: "all 0.3s ease",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
                    }}
                    onMouseEnter={(e) => {
                      if (connectivityScrollPosition !== 0) {
                        e.currentTarget.style.borderColor = brandPurple;
                        e.currentTarget.style.background = "#F3E8FF";
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = "#E5E7EB";
                      e.currentTarget.style.background = "#fff";
                    }}
                  >
                    <ChevronLeft size={20} color={connectivityScrollPosition === 0 ? "#ccc" : brandPurple} />
                  </button>
                  <button
                    onClick={() => scrollConnectivity('right')}
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      border: "2px solid #E5E7EB",
                      background: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = brandPurple;
                      e.currentTarget.style.background = "#F3E8FF";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = "#E5E7EB";
                      e.currentTarget.style.background = "#fff";
                    }}
                  >
                    <ChevronRight size={20} color={brandPurple} />
                  </button>
                </div>
              </div>

              {/* Connectivity Section - Responsive Grid Layout */}
              <div style={{
                display: "grid",
                // Creates as many columns as fit (minimum 280px each), filling the row
                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                gap: "20px",
                marginBottom: "32px"
              }}>
                {[
                  { label: "MIDC Zones", value: Array.isArray(district.midcZones) ? district.midcZones.join(", ") : district.midcZones, icon: <Building2 size={28} strokeWidth={1.8} />, gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" },
                  { label: "Highways", value: district.highways, icon: <Truck size={28} strokeWidth={1.8} />, gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)" },
                  { label: "Railways", value: district.railways, icon: <Train size={28} strokeWidth={1.8} />, gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)" },
                  { label: "Nearest Airport", value: district.nearestAirport, icon: <Plane size={28} strokeWidth={1.8} />, gradient: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)" },
                  { label: "Nearest Port", value: district.nearestPort, icon: <Anchor size={28} strokeWidth={1.8} />, gradient: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)" }
                ].map((item, i) => (
                  <div
                    key={i}
                    style={{
                      padding: "24px 20px",
                      background: "#fff",
                      borderRadius: "16px",
                      boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
                      border: "1px solid #F0F0F0",
                      position: "relative",
                      overflow: "hidden",
                      transition: "all 0.3s ease",
                      cursor: "pointer"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-6px)";
                      e.currentTarget.style.boxShadow = "0 12px 32px rgba(60,0,122,0.12)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.06)";
                    }}
                  >
                    {/* Gradient background decoration */}
                    <div style={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      width: "100px",
                      height: "100px",
                      background: item.gradient,
                      opacity: 0.08,
                      borderRadius: "0 16px 0 100%"
                    }} />

                    {/* Icon container */}
                    <div style={{
                      width: "52px",
                      height: "52px",
                      borderRadius: "12px",
                      background: item.gradient,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: "16px",
                      boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                      color: "#fff",
                      position: "relative",
                      zIndex: 1
                    }}>
                      {item.icon}
                    </div>

                    {/* Label */}
                    <div style={{
                      fontSize: "21px",
                      fontWeight: 500,
                      color: textDark,
                      textTransform: "uppercase",
                      letterSpacing: "0.8px",
                      marginBottom: "15px",
                      position: "relative",
                      zIndex: 1
                    }}>
                      {item.label}
                    </div>

                    {/* Value */}
                    <div style={{
                      fontSize: "16px",
                      fontWeight: 400,
                      color: textMuted,
                      lineHeight: "1.5",
                      minHeight: "48px",
                      position: "relative",
                      zIndex: 1
                    }}>
                      {item.value || "—"}
                    </div>
                  </div>
                ))}
              </div>

              {/* Connectivity Details */}
              {district.connectivityDetails && (
                <div style={{
                  padding: "28px",
                  background: "#fff",
                  border: "1px solid #E5E7EB",
                  borderRadius: "16px",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.03)",
                  position: "relative",
                  overflow: "hidden"
                }}>
                  <div style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "4px",
                    background: `linear-gradient(90deg, ${brandPurple} 0%, ${brandOrange} 100%)`
                  }} />
                  <h4 style={{
                    margin: "0 0 16px 0",
                    fontSize: "24px",
                    color: brandPurple,
                    fontWeight: 400,
                    display: "flex",
                    alignItems: "center",
                    gap: "10px"
                  }}>
                    <Info size={24} /> Connectivity Analysis
                  </h4>
                  <p style={{
                    margin: 0,
                    color: textMuted,
                    lineHeight: 1.8,
                    fontSize: "17px"
                  }}>
                    {district.connectivityDetails}
                  </p>
                </div>
              )}
            </section>
          </div>

        );
      case 'chair':
        return (
          <div style={{
            background: "#fff",
            border: "1px solid #f0f0f0",
            borderRadius: 16,
            padding: 32,
            boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
            fontFamily: "'Rubik', sans-serif"
          }}>
            {/* Section Header with Professional Accent */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '50px' }}>
              {/* <div style={{ width: '6px', height: '30px', background: '#FF8F00', borderRadius: '4px' }} /> */}
              <h4 style={{ margin: 0, color: "#3C007A", fontSize: "24px", fontWeight: 400 }}>
                Conveners & Co-Conveners
              </h4>
            </div>

            {Array.isArray(district.chairCoChairs) && district.chairCoChairs.length ? (
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))",
                gap: 24
              }}>
                {district.chairCoChairs.map((p, i) => {
                  const photoSrc = p.photo ? `${process.env.REACT_APP_API_BASE_URL}/uploads/${p.photo}` : null;
                  const initials = (p.name || "Member").trim().split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
                  const isChair = p.role === 'Chair' || p.role === 'Convener';
                  const displayRole = (role) => {
                    if (!role) return 'Member';
                    if (role === 'Chair') return 'Convener';
                    if (role === 'Co-Chair') return 'Co-Convener';
                    return role;
                  };

                  return (
                    <div
                      key={i}
                      style={{
                        background: "#ffffff",
                        borderRadius: 20,
                        padding: "24px",
                        border: "1px solid #f1f1f1",
                        boxShadow: "0 10px 30px rgba(0,0,0,0.03)",
                        transition: "all 0.3s ease",
                        position: "relative",
                        overflow: "hidden"
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-8px)";
                        e.currentTarget.style.borderColor = "#3C007A";
                        e.currentTarget.style.boxShadow = "0 20px 40px rgba(60, 0, 122, 0.08)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.borderColor = "#f1f1f1";
                        e.currentTarget.style.boxShadow = "0 10px 30px rgba(0,0,0,0.03)";
                      }}
                    >
                      {/* Visual Role Indicator Tab */}
                      <div style={{
                        position: "absolute",
                        top: 4,
                        right: 16,
                        padding: "4px 12px",
                        background: isChair ? "#3C007A" : "#FF8F00",
                        color: "#fff",
                        fontSize: "13px",
                        fontWeight: 400,
                        // textTransform: "uppercase",
                        letterSpacing: "1px",
                        borderRadius: "5px",

                      }}>
                        {displayRole(p.role)}
                      </div>

                      <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
                        {/* High-Quality Avatar */}
                        <div style={{
                          width: 100,
                          height: 100,
                          borderRadius: 16, // Modern "squircle"
                          background: "#F3F4F6",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          border: "3px solid #fff",
                          boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                          overflow: "hidden",
                          flexShrink: 0
                        }}>
                          {photoSrc ? (
                            <img src={photoSrc} alt={p.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          ) : (
                            <div style={{ color: "#3C007A", fontSize: "32px", fontWeight: 600 }}>{initials}</div>
                          )}
                        </div>

                        <div style={{ flex: 1, paddingTop: 30 }}>
                          <div style={{ color: "#333333", fontWeight: 500, fontSize: "20px", marginBottom: "4px" }}>
                            {p.name || "Member Name"}
                          </div>
                          <div style={{ color: "#FF8F00", fontWeight: 400, fontSize: "14px", letterSpacing: "0.5px" }}>
                            {p.postName || "Position Not Specified"}
                          </div>
                        </div>
                      </div>

                      {/* Divider Line */}
                      <div style={{ height: "1px", background: "linear-gradient(to right, #f0f0f0, #ffffff)", margin: "10px 0" }} />

                      {/* Bio/Description Section */}
                      {p.description && (
                        <div>

                          <p style={{
                            color: "#666",
                            fontSize: "15px",
                            lineHeight: "1.7",
                            fontWeight: 400, // Strictly weight 400 for prose
                            margin: 0
                          }}>
                            {p.description.length > 180 ? `${p.description.slice(0, 180)}…` : p.description}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{
                padding: "60px 0",
                textAlign: "center",
                background: "#F9FAFB",
                borderRadius: 16,
                border: "2px dashed #E5E7EB"
              }}>
                <div style={{ color: "#999", fontSize: "16px", fontWeight: 400 }}>No leadership members added.</div>
              </div>
            )}
          </div>
        );
      case 'industrial':
        return (
          <div style={{
            fontFamily: "'Rubik', sans-serif",
            backgroundColor: "#F8F9FA",
            padding: "20px"
          }}>
            {/* Industrial Profile & Growth */}
            <section style={{ marginBottom: "40px" }}>
              <div style={{
                fontFamily: "'Rubik', sans-serif",
                padding: "10px 20px",
                backgroundColor: "#F8F9FA",
                borderRadius: "16px",
                marginTop: "5px",
                marginBottom: "10px"
              }}>
                {/* Header Section with Dashed Outline */}
                <div style={{
                  border: "2px dashed #E5E7EB",
                  borderRadius: "100px",
                  padding: "12px 40px",
                  width: "fit-content",
                  margin: "0 auto 60px auto",
                  textAlign: "center",
                  position: "relative",
                  backgroundColor: "#fff"
                }}>
                  <h3 style={{
                    margin: 0,
                    fontSize: "21px",
                    fontWeight: 500,
                    color: "#333"
                  }}>
                    Industrial Profile & Growth
                  </h3>

                  {/* Downward Connectors mapped to follow the grid columns */}
                  <div style={{
                    position: "absolute",
                    bottom: "-48px",
                    left: "0",
                    right: "0",
                    display: "flex",
                    justifyContent: "space-around",
                    zIndex: 0
                  }}>
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} style={{
                        height: "48px",
                        width: "2px",
                        borderLeft: "2px dashed #FF8F00"
                      }} />
                    ))}
                  </div>
                </div>

                {/* Ranking Style Cards Grid */}
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                  gap: "24px",
                  position: "relative"
                }}>
                  {[
                    {
                      label: "Industrial Zone",
                      value: district.industrialZone,
                      icon: <Factory size={24} />,
                      highlight: true,
                      year: "Primary Zone"
                    },
                    {
                      label: "MSME Count",
                      value: district.msmeCount,
                      icon: <Building2 size={24} />,
                      highlight: false,
                      year: "Active Units"
                    },
                    {
                      label: "Large Industries",
                      value: district.largeIndustriesCount,
                      icon: <Ship size={24} />,
                      highlight: false,
                      year: "Registered"
                    },
                    {
                      label: "Growth Rate",
                      value: district.industrialGrowthRate ? `${district.industrialGrowthRate}%` : "N/A",
                      icon: <TrendingUp size={24} />,
                      highlight: false,
                      year: "Annual YoY"
                    }
                  ].map((stat, idx) => (
                    <div
                      key={idx}
                      style={{
                        background: stat.highlight ? "#FFFBEB" : "#fff",
                        border: stat.highlight ? "2px dashed #FFD000" : "1px dashed #E5E7EB",
                        borderRadius: "16px",
                        padding: "32px 16px",
                        textAlign: "center",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        minHeight: "200px",
                        transition: "all 0.3s ease",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
                        cursor: "pointer",
                        fontFamily: "'Rubik', sans-serif"
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-8px)";
                        e.currentTarget.style.boxShadow = "0 12px 24px rgba(60,0,122,0.15)";
                        e.currentTarget.style.borderColor = stat.highlight ? "#FF8F00" : "#3C007A";
                        e.currentTarget.style.background = stat.highlight ? "#FFF8E1" : "#F9F5FF";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.03)";
                        e.currentTarget.style.borderColor = stat.highlight ? "#FFD000" : "#E5E7EB";
                        e.currentTarget.style.background = stat.highlight ? "#FFFBEB" : "#fff";
                      }}
                    >
                      {/* Icon Circle */}
                      <div style={{
                        width: "56px",
                        height: "56px",
                        borderRadius: "50%",
                        backgroundColor: stat.highlight ? "#FF8F00" : "#14B8A6",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#fff",
                        marginBottom: "20px",
                        boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
                        position: "relative",
                        zIndex: 2,
                        transition: "all 0.3s ease"
                      }}>
                        {stat.icon}
                      </div>

                      {/* Label */}
                      <h4 style={{
                        margin: "0 0 12px 0",
                        fontSize: "16px",
                        fontWeight: 400,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        color: stat.highlight ? "#FF8F00" : "#3C007A",
                        fontFamily: "'Rubik', sans-serif"
                      }}>
                        {stat.label}
                      </h4>

                      {/* Value */}
                      <div style={{
                        fontSize: "28px",
                        fontWeight: 600,
                        color: "#333333",
                        marginBottom: "8px",
                        fontFamily: "'Rubik', sans-serif"
                      }}>
                        {stat.value || "—"}
                      </div>

                      {/* Sub-text / Year */}
                      <div style={{
                        fontSize: "16px",
                        fontWeight: 400,
                        color: "#666666",
                        marginTop: "auto",
                        paddingTop: "10px",
                        borderTop: "1px solid #f0f0f0",
                        width: "100%",
                        fontFamily: "'Rubik', sans-serif"
                      }}>
                        {stat.year}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Major Industries Overview Card */}
              {district.majorIndustries && (
                <div style={{
                  background: "#fff",
                  borderRadius: "16px",
                  padding: "28px",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
                  border: "1px solid #F0F0F0",
                  position: "relative",
                  overflow: "hidden"
                }}>
                  <div style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "4px",
                    // background: "linear-gradient(90deg, #3C007A 0%, #FF8F00 100%)"
                  }} />
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    marginBottom: "16px"
                  }}>
                    <div style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "10px",
                      background: "linear-gradient(135deg, #3C007A 0%, #5B2A86 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff"
                    }}>
                      <Building size={22} />
                    </div>
                    <h3 style={{
                      margin: 0,
                      fontSize: "21px",
                      fontWeight: 500,
                      color: "#3C007A",
                      fontFamily: "'Rubik', sans-serif"
                    }}>
                      Major Industries Overview
                    </h3>
                  </div>
                  <p style={{
                    margin: 0,
                    fontSize: "17px",
                    lineHeight: 1.8,
                    color: "#666666",
                    fontFamily: "'Rubik', sans-serif"
                  }}>
                    {district.majorIndustries}
                  </p>
                </div>
              )}
            </section>

            {/* Power & Utilities Section */}
            <section style={{ marginBottom: "60px", padding: "20px", fontFamily: "'Rubik', sans-serif" }}>
              {/* Header with Color #3C007A */}
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                marginBottom: "60px", // Extra space for floating icons
                borderBottom: "2px solid #E5E7EB",
                paddingBottom: "12px"
              }}>
                <div style={{
                  width: "6px",
                  height: "28px",
                  backgroundColor: "#FF8F00",
                  borderRadius: "4px"
                }} />
                <Title level={2} style={{
                  fontSize: "21px",
                  fontWeight: 500,
                  margin: 0,
                  color: "#3C007A",
                  fontFamily: "'Rubik', sans-serif"
                }}>
                  Power & Utilities Infrastructure
                </Title>
              </div>

              <Row gutter={[24, 48]}>
                {infrastructureData.map((item, i) => (
                  <Col xs={24} sm={12} lg={6} key={i}>
                    <Card
                      hoverable
                      bordered={false}
                      style={{
                        borderRadius: "16px",
                        textAlign: "center",
                        overflow: "visible",
                        background: "#ffffff",
                        boxShadow: "0 8px 20px rgba(0,0,0,0.06)",
                        borderTop: `5px solid ${item.color}`,
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        height: "200px",
                        position: "relative"
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-8px)";
                        e.currentTarget.style.boxShadow = "0 16px 40px rgba(60,0,122,0.15)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "0 8px 20px rgba(0,0,0,0.06)";
                      }}
                    >
                      {/* Floating Circle Icon */}
                      <div style={{
                        position: "absolute",
                        top: "-40px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: "75px",
                        height: "75px",
                        borderRadius: "50%",
                        backgroundColor: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 8px 20px rgba(0,0,0,0.12)",
                        zIndex: 2,
                        fontSize: "32px",
                        color: item.color,
                        transition: "all 0.3s ease"
                      }}>
                        {item.icon}
                      </div>

                      <div style={{ marginTop: "5px", padding: "0 2px" }}>
                        {/* Value - Primary Data Point */}
                        <div style={{
                          fontSize: "17px",
                          fontWeight: 500,
                          color: "#3C007A",
                          marginBottom: "15px",
                          fontFamily: "'Rubik', sans-serif",
                          minHeight: "48px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center"
                        }}>
                          {item.value || "—"}
                        </div>

                        {/* Label - Secondary Metadata */}
                        <Text style={{
                          fontSize: "16px",
                          fontWeight: 400,
                          color: "#666666",
                          fontFamily: "'Rubik', sans-serif",
                          display: "block",
                          lineHeight: "1.5"
                        }}>
                          {item.label}
                        </Text>
                      </div>
                    </Card>
                  </Col>
                ))}
              </Row>




              {/* Power Details Card */}
              {district.powerDetails && (
                <div style={{
                  background: "linear-gradient(135deg, #F3E8FF 0%, #FFF5E6 100%)",
                  borderRadius: "16px",
                  padding: "24px",
                  border: "1px solid #E5D4FF",
                  boxShadow: "0 4px 16px rgba(60,0,122,0.06)"
                }}>
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    marginBottom: "12px"
                  }}>
                    <Info size={20} color="#3C007A" />
                    <h4 style={{
                      margin: 0,
                      fontSize: "18px",
                      fontWeight: 600,
                      color: "#3C007A",
                      fontFamily: "'Rubik', sans-serif"
                    }}>
                      Additional Power Details
                    </h4>
                  </div>
                  <p style={{
                    margin: 0,
                    fontSize: "16px",
                    lineHeight: 1.8,
                    color: "#666666",
                    fontFamily: "'Rubik', sans-serif"
                  }}>
                    {district.powerDetails}
                  </p>
                </div>
              )}
            </section>

            {/* Talent & Education Section */}
            <section style={{ marginBottom: "60px", padding: "20px", fontFamily: "'Rubik', sans-serif" }}>
              {/* Section Header */}
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                marginBottom: "40px",
                borderBottom: "2px solid #E5E7EB",
                paddingBottom: "12px"
              }}>
                <div style={{
                  width: "6px",
                  height: "28px",
                  backgroundColor: "#FF8F00",
                  borderRadius: "4px"
                }} />
                <Title level={2} style={{
                  fontSize: "21px",
                  fontWeight: 500,
                  margin: 0,
                  color: "#3C007A",
                  fontFamily: "'Rubik', sans-serif"
                }}>
                  Talent & Education Ecosystem
                </Title>
              </div>

              <Row gutter={[24, 24]}>
                {/* Main Overview Cards */}
                {mainStats.map((item, i) => (
                  <Col xs={24} lg={12} key={i}>
                    <Card
                      hoverable
                      bordered={false}
                      style={{
                        borderRadius: "20px",
                        overflow: "hidden",
                        boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                        background: "#ffffff",
                        minHeight: "260px"
                      }}
                    >
                      <div style={{ display: "flex", gap: "20px" }}>
                        {/* Large Gradient Icon */}
                        <div style={{
                          minWidth: "70px",
                          height: "70px",
                          borderRadius: "18px",
                          background: item.gradient,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "32px",
                          color: "#fff",
                          boxShadow: "0 10px 20px rgba(0,0,0,0.1)"
                        }}>
                          {item.icon}
                        </div>

                        <div style={{ flex: 1 }}>
                          <Space style={{ marginBottom: "8px" }}>
                            {item.tags.map(tag => (
                              <Tag key={tag} color="blue" style={{ borderRadius: '10px', fontSize: '11px' }}>{tag}</Tag>
                            ))}
                          </Space>
                          <Title level={4} style={{
                            marginTop: 0,
                            color: "#3C007A",
                            fontSize: "18px",
                            // textTransform: "uppercase",
                            letterSpacing: "0.5px"
                          }}>
                            {item.label}
                          </Title>
                          <Paragraph style={{
                            fontSize: "16px",
                            color: "#666",
                            lineHeight: 1.7,
                            margin: 0,
                            fontWeight: 400
                          }}>
                            {item.value || "Strategic ecosystem supporting local industrial growth through a steady pipeline of skilled manpower."}
                          </Paragraph>
                        </div>
                      </div>

                      {/* Subtle background decoration */}
                      <div style={{
                        position: "absolute",
                        bottom: "-20px",
                        right: "-20px",
                        width: "100px",
                        height: "100px",
                        background: item.gradient,
                        opacity: 0.05,
                        borderRadius: "50%"
                      }} />
                    </Card>
                  </Col>
                ))}

                {/* Detailed Metric Grid */}

              </Row>
            </section>
          </div>
        );
      case 'resources':
        return (
          <div style={{
            background: "#fff",
            border: "1px solid #eee",
            borderRadius: 16,
            padding: 32,
            boxShadow: "0 2px 15px rgba(0,0,0,0.05)",
            marginBottom: 24,
            fontFamily: "'Rubik', sans-serif"
          }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24, gap: 12 }}>
              {/* <div style={{ width: 6, height: 24, background: '#FF8F00', borderRadius: 4 }} /> */}
              <h2 style={{ margin: 0, color: "#3C007A", fontSize: "24px", fontWeight: 400 }}>
                Natural Resources
              </h2>
            </div>

            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
              gap: "32px"
            }}>
              {resources.map((res, index) => (
                <ResourceHoverCard
                  key={index}
                  {...res}
                />
              ))}
            </div>
          </div>
        );
      case 'incentives':
        return (
          <div style={{
            background: "#fff",
            padding: "24px",
            borderRadius: "16px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
            fontFamily: "'Rubik', sans-serif"
          }}>
            {/* Section Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '80px' }}>
              {/* <div style={{ width: '6px', height: '28px', background: '#FF8F00', borderRadius: '4px' }} /> */}
              <Title level={4} style={{ margin: 0, color: "#3C007A", fontSize: "24px", fontWeight: 400 }}>
                Industrial Incentives & Benefits
              </Title>
            </div>

            {/* Grid Layout */}
            <Row gutter={[24, 48]}>
              {incentives.map((item, index) => (
                <Col xs={24} sm={12} lg={6} key={index}>
                  <Card
                    hoverable
                    bordered={false}
                    style={{
                      borderRadius: "16px",
                      textAlign: "center",
                      overflow: "visible",
                      background: item.available ? "#F9F5FF" : "#FFFFFF",
                      boxShadow: item.available
                        ? "0 10px 25px rgba(60, 0, 122, 0.08)"
                        : "0 8px 20px rgba(0,0,0,0.04)",
                      borderTop: `5px solid ${item.available ? "#3C007A" : "#E5E7EB"}`,
                      transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)", // Smooth elastic transition
                      height: "100%",
                      position: "relative"
                    }}
                    // --- HOVER EFFECTS START ---
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-12px)";
                      e.currentTarget.style.boxShadow = item.available
                        ? "0 20px 40px rgba(60, 0, 122, 0.15)"
                        : "0 15px 30px rgba(0,0,0,0.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = item.available
                        ? "0 10px 25px rgba(60, 0, 122, 0.08)"
                        : "0 8px 20px rgba(0,0,0,0.04)";
                    }}
                  // --- HOVER EFFECTS END ---
                  >
                    {/* Floating Circle Icon */}
                    <div style={{
                      position: "absolute",
                      top: "-40px",
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: "75px",
                      height: "75px",
                      borderRadius: "50%",
                      backgroundColor: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 8px 20px rgba(0,0,0,0.12)",
                      zIndex: 2,
                      fontSize: "30px",
                      color: item.available ? "#3C007A" : "#666666",
                      border: `2px solid ${item.available ? "#3C007A" : "#F0F0F0"}`,
                      transition: "all 0.3s ease" // Smooth icon color transition
                    }}>
                      {item.icon}
                    </div>

                    <div style={{ marginTop: "25px" }}>
                      <Title level={5} style={{
                        color: "#3C007A",
                        margin: "0 0 12px 0",
                        fontSize: "18px",
                        fontWeight: 500,
                        fontFamily: "'Rubik', sans-serif"
                      }}>
                        {item.title}
                      </Title>

                      <Tag color={item.available ? "purple" : "default"} style={{ marginBottom: "12px", borderRadius: "10px" }}>
                        {item.available ? "Available" : "Not Available"}
                      </Tag>

                      <Paragraph style={{
                        color: "#666666",
                        fontSize: "15px",
                        fontWeight: 400,
                        lineHeight: "1.5",
                        margin: 0,
                        fontFamily: "'Rubik', sans-serif"
                      }}>
                        {item.desc}
                      </Paragraph>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>

            {/* Additional Incentives Section */}
            {district.otherIncentives && (
              <Card
                style={{
                  marginTop: "48px",
                  borderRadius: "12px",
                  background: "#FDFDFD",
                  border: "1px dashed #FF8F00"
                }}
                bodyStyle={{ padding: "24px" }}
              >
                <Space align="start" size="middle">
                  {/* <InfoCircleOutlined style={{ fontSize: '24px', color: '#FF8F00' }} /> */}
                  <div>
                    <Text style={{ color: "#3C007A", fontSize: "21px", fontWeight: 500, display: "block", marginBottom: "8px" }}>
                      Additional Incentives
                    </Text>
                    <Text style={{ color: "#666666", fontSize: "16px", fontWeight: 400, lineHeight: 1.7 }}>
                      {district.otherIncentives}
                    </Text>
                  </div>
                </Space>
              </Card>
            )}
          </div>
        );
      case 'opportunities':
        return (
          <div style={{
            background: "#fff",
            padding: "32px",
            borderRadius: "16px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
            fontFamily: "'Rubik', sans-serif"
          }}>
            {/* Section Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '40px',
              borderBottom: '2px solid #E5E7EB',
              paddingBottom: '12px'
            }}>
              {/* <div style={{
                width: '6px',
                height: '28px',
                background: '#FF8F00',
                borderRadius: '4px'
              }} /> */}
              <Title level={2} style={{
                margin: 0,
                color: "#3C007A",
                fontSize: "24px",
                fontWeight: 400,
                fontFamily: "'Rubik', sans-serif"
              }}>
                Investment Opportunities
              </Title>
            </div>

            {Array.isArray(district.investmentOpportunities) && district.investmentOpportunities.length ? (
              <Row gutter={[24, 24]}>
                {district.investmentOpportunities.map((op, index) => (
                  <Col xs={24} sm={12} lg={8} key={index}>
                    <Card
                      hoverable
                      bordered={false}
                      style={{
                        borderRadius: "16px",
                        overflow: "hidden",
                        background: "#FFFFFF",
                        boxShadow: "0 8px 20px rgba(0,0,0,0.06)",
                        border: "1px solid #F0F0F0",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        height: "100%",
                        minHeight: "280px"
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-8px)";
                        e.currentTarget.style.boxShadow = "0 16px 40px rgba(60,0,122,0.15)";
                        e.currentTarget.style.borderColor = "#3C007A";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "0 8px 20px rgba(0,0,0,0.06)";
                        e.currentTarget.style.borderColor = "#F0F0F0";
                      }}
                    >
                      {/* Gradient Background Decoration */}
                      <div style={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        width: "120px",
                        height: "120px",
                        background: "linear-gradient(135deg, #3C007A 0%, #FF8F00 100%)",
                        opacity: 0.05,
                        borderRadius: "0 16px 0 100%"
                      }} />

                      <div style={{ position: "relative", zIndex: 1 }}>
                        {/* Icon Container */}
                        <div style={{
                          width: "64px",
                          height: "64px",
                          borderRadius: "16px",
                          background: "linear-gradient(135deg, #3C007A 0%, #5B2A86 100%)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          marginBottom: "20px",
                          boxShadow: "0 8px 16px rgba(60,0,122,0.2)",
                          color: "#fff",
                          transition: "all 0.3s ease"
                        }}>
                          <Handshake size={32} strokeWidth={1.5} />
                        </div>

                        {/* Sector Title */}
                        <Title level={4} style={{
                          margin: "0 0 12px 0",
                          color: "#3C007A",
                          fontSize: "19px",
                          fontWeight: 600,
                          lineHeight: "1.4",
                          fontFamily: "'Rubik', sans-serif"
                        }}>
                          {op.sector || "Investment Opportunity"}
                        </Title>

                        {/* Divider */}
                        <div style={{
                          height: "2px",
                          width: "40px",
                          background: "#FF8F00",
                          marginBottom: "16px",
                          borderRadius: "2px"
                        }} />

                        {/* Rationale/Description */}
                        <Paragraph style={{
                          margin: 0,
                          color: "#666666",
                          fontSize: "15px",
                          fontWeight: 400,
                          lineHeight: "1.7",
                          minHeight: "80px",
                          fontFamily: "'Rubik', sans-serif"
                        }}>
                          {op.rationale || "Explore investment potential in this sector with strategic advantages and growth opportunities."}
                        </Paragraph>

                        {/* Optional: Investment Badge */}
                        <div style={{
                          marginTop: "20px",
                          paddingTop: "16px",
                          borderTop: "1px solid #F0F0F0"
                        }}>
                          <Tag
                            color="purple"
                            style={{
                              borderRadius: "20px",
                              padding: "4px 12px",
                              fontSize: "13px",
                              fontWeight: 500,
                              border: "none",
                              background: "#F3E8FF",
                              color: "#3C007A"
                            }}
                          >
                            Investment Ready
                          </Tag>
                        </div>
                      </div>
                    </Card>
                  </Col>
                ))}
              </Row>
            ) : (
              <div style={{
                padding: "60px 20px",
                textAlign: "center",
                background: "#F8F9FA",
                borderRadius: "12px",
                border: "2px dashed #E5E7EB"
              }}>
                <div style={{
                  width: "80px",
                  height: "80px",
                  borderRadius: "50%",
                  background: "#F3E8FF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 16px auto"
                }}>
                  <AlertCircle size={40} color="#3C007A" />
                </div>
                <Text style={{
                  color: "#999999",
                  fontSize: "17px",
                  fontWeight: 400,
                  fontFamily: "'Rubik', sans-serif",
                  display: "block"
                }}>
                  No investment opportunities listed at the moment.
                </Text>
                <Text style={{
                  color: "#CCCCCC",
                  fontSize: "15px",
                  fontWeight: 400,
                  fontFamily: "'Rubik', sans-serif",
                  display: "block",
                  marginTop: "8px"
                }}>
                  Check back later for new opportunities
                </Text>
              </div>
            )}
          </div>
        );
      case 'policies':
        return (
          <div style={{
            background: "#fff",
            border: "1px solid #eee",
            borderRadius: 10,
            padding: 24,
            boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
            marginBottom: 24,
            fontFamily: "'Rubik', sans-serif"
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
              <h4 style={{ margin: 0, color: "#3C007A", fontSize: "24px", fontWeight: 400, fontFamily: "'Rubik', sans-serif" }}>
                Policies
              </h4>
              {Array.isArray(district.policies) && district.policies.length > 2 && (
                <div style={{ display: "flex", gap: 12 }}>
                  {/* Functional navigation buttons */}
                  <button onClick={() => scrollPolicies('left')} style={navButtonStyle}>
                    <ChevronLeft size={20} />
                  </button>
                  <button onClick={() => scrollPolicies('right')} style={navButtonStyle}>
                    <ChevronRight size={20} />
                  </button>
                </div>
              )}
            </div>

            {Array.isArray(district.policies) && district.policies.length ? (
              <div
                ref={policiesContainerRef}
                style={{
                  display: "flex",
                  gap: 20,
                  overflowX: "auto",
                  padding: "10px 5px",
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                  WebkitOverflowScrolling: "touch"
                }}
              >
                {district.policies.map((p, i) => (
                  <div
                    key={i}
                    style={{
                      minWidth: 320,
                      maxWidth: 320,
                      padding: "30px 24px",
                      background: "linear-gradient(135deg, rgba(60, 0, 122, 0.05) 0%, rgba(60, 0, 122, 0.08) 100%)",
                      borderRadius: 16,
                      position: "relative",
                      boxShadow: "0 4px 15px rgba(60, 0, 122, 0.1)",
                      transition: "all 0.3s ease",
                      cursor: "pointer",
                      border: "1px solid rgba(60, 0, 122, 0.15)",
                      fontFamily: "'Rubik', sans-serif"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-6px)";
                      e.currentTarget.style.boxShadow = "0 12px 25px rgba(60, 0, 122, 0.15)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "0 4px 15px rgba(60, 0, 122, 0.1)";
                    }}
                  >
                    {/* Top-right Download Tab */}
                    <div style={{
                      position: "absolute",
                      top: 0,
                      right: 10,
                      width: 48,
                      height: 60,
                      background: "#fff",
                      borderRadius: "0 0 12px 12px",
                      boxShadow: "0 4px 10px rgba(60, 0, 122, 0.15)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}>
                      <Download size={22} color="#3C007A" strokeWidth={2.5} />
                    </div>

                    {/* Policy Graphic Icon Container */}


                    {/* Text Content */}
                    <h5 style={{
                      margin: "0 0 12px 0",
                      color: "#3C007A",
                      fontSize: "18px",
                      fontWeight: 400,
                      lineHeight: 1.3,
                      marginTop: "5px",
                      fontFamily: "'Rubik', sans-serif",
                      marginRight: "30px"
                    }}>
                      {p.name || "Policy"}
                    </h5>

                    <p style={{
                      margin: 0,
                      color: "#666666",
                      fontSize: "16px",
                      fontWeight: 400,
                      lineHeight: 1.6,
                      fontFamily: "'Rubik', sans-serif"
                    }}>
                      {p.description || "Policy details and information"}
                    </p>

                    {p.date && (
                      <>
                        <Divider style={{ margin: "20px 0 16px 0", borderColor: "rgba(60, 0, 122, 0.15)" }} />
                        <div style={{
                          fontSize: "16px",
                          color: "#333333",
                          fontWeight: 400,
                          fontFamily: "'Rubik', sans-serif",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "8px"
                        }}>
                          <CalendarOutlined style={{ color: "#3C007A", fontSize: "16px" }} />
                          {p.date}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div style={{
                padding: "40px 0",
                textAlign: "center",
                color: "#666666",
                fontFamily: "'Rubik', sans-serif",
                fontSize: "17px",
                fontWeight: 400
              }}>
                No policies available.
              </div>
            )}
          </div>
        );
      case 'schemes':
        return (
          <div style={{
            background: "#fff",
            border: "1px solid #eee",
            borderRadius: 10,
            padding: 24,
            boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
            marginBottom: 24,
            fontFamily: "'Rubik', sans-serif"
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
              <h4 style={{ margin: 0, color: "#3C007A", fontSize: "24px", fontWeight: 400, fontFamily: "'Rubik', sans-serif" }}>
                Schemes
              </h4>
              {Array.isArray(district.schemes) && district.schemes.length > 2 && (
                <div style={{ display: "flex", gap: 12 }}>
                  <button onClick={() => scrollSchemes('left')} style={navButtonStyle}>
                    <ChevronLeft size={20} />
                  </button>
                  <button onClick={() => scrollSchemes('right')} style={navButtonStyle}>
                    <ChevronRight size={20} />
                  </button>
                </div>
              )}
            </div>

            {Array.isArray(district.schemes) && district.schemes.length ? (
              <div
                ref={schemesContainerRef}
                style={{
                  display: "flex",
                  gap: 20,
                  overflowX: "auto",
                  padding: "10px 5px",
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                  WebkitOverflowScrolling: "touch"
                }}
              >
                {district.schemes.map((s, i) => (
                  <div
                    key={i}
                    style={{
                      minWidth: 320,
                      maxWidth: 320,
                      padding: "30px 24px",
                      background: "linear-gradient(135deg, rgba(60, 0, 122, 0.05) 0%, rgba(60, 0, 122, 0.08) 100%)",
                      borderRadius: 16,
                      position: "relative",
                      boxShadow: "0 4px 15px rgba(60, 0, 122, 0.1)",
                      transition: "all 0.3s ease",
                      cursor: "pointer",
                      border: "1px solid rgba(60, 0, 122, 0.15)",
                      fontFamily: "'Rubik', sans-serif"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-6px)";
                      e.currentTarget.style.boxShadow = "0 12px 25px rgba(60, 0, 122, 0.15)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "0 4px 15px rgba(60, 0, 122, 0.1)";
                    }}
                  >
                    {/* Top-right Download Tab */}
                    <div style={{
                      position: "absolute",
                      top: 0,
                      right: 10,
                      width: 48,
                      height: 60,
                      background: "#fff",
                      borderRadius: "0 0 12px 12px",
                      boxShadow: "0 4px 10px rgba(60, 0, 122, 0.15)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}>
                      <Download size={22} color="#3C007A" strokeWidth={2.5} />
                    </div>

                    {/* Text Content */}
                    <h5 style={{
                      margin: "0 0 12px 0",
                      color: "#3C007A",
                      fontSize: "20px",
                      fontWeight: 400,
                      lineHeight: 1.3,
                      marginTop: "5px",
                      fontFamily: "'Rubik', sans-serif",
                      marginRight: "30px"
                    }}>
                      {s.name || "Scheme"}
                    </h5>

                    <p style={{
                      margin: 0,
                      color: "#666666",
                      fontSize: "17px",
                      fontWeight: 400,
                      lineHeight: 1.6,
                      fontFamily: "'Rubik', sans-serif"
                    }}>
                      {s.description || "Scheme details and information"}
                    </p>

                    {s.date && (
                      <>
                        <Divider style={{ margin: "20px 0 16px 0", borderColor: "rgba(60, 0, 122, 0.15)" }} />
                        <div style={{
                          fontSize: "16px",
                          color: "#333333",
                          fontWeight: 400,
                          fontFamily: "'Rubik', sans-serif",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "8px"
                        }}>
                          <CalendarOutlined style={{ color: "#3C007A", fontSize: "16px" }} />
                          {s.date}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div style={{
                padding: "40px 0",
                textAlign: "center",
                color: "#666666",
                fontFamily: "'Rubik', sans-serif",
                fontSize: "17px",
                fontWeight: 400
              }}>
                No schemes available.
              </div>
            )}
          </div>
        );
      case 'press':
        return (
          <div style={{
            background: "#fff",
            border: "1px solid #eee",
            borderRadius: 10,
            padding: 24,
            boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
            marginBottom: 24,
            fontFamily: "'Rubik', sans-serif"
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
              <h4 style={{ margin: 0, color: "#3C007A", fontSize: "24px", fontWeight: 400, fontFamily: "'Rubik', sans-serif" }}>
                Press Releases
              </h4>
              {Array.isArray(district.pressReleases) && district.pressReleases.length > 2 && (
                <div style={{ display: "flex", gap: 12 }}>
                  <button onClick={() => scrollPress('left')} style={navButtonStyle}>
                    <ChevronLeft size={20} />
                  </button>
                  <button onClick={() => scrollPress('right')} style={navButtonStyle}>
                    <ChevronRight size={20} />
                  </button>
                </div>
              )}
            </div>

            {Array.isArray(district.pressReleases) && district.pressReleases.length ? (
              <div
                ref={pressContainerRef}
                style={{
                  display: "flex",
                  gap: 20,
                  overflowX: "auto",
                  padding: "10px 5px",
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                  WebkitOverflowScrolling: "touch"
                }}
              >
                {district.pressReleases.map((p, i) => (
                  <div
                    key={i}
                    style={{
                      minWidth: 360,
                      maxWidth: 360,
                      padding: "32px 28px",
                      background: "linear-gradient(135deg, rgba(255, 143, 0, 0.03) 0%, rgba(255, 143, 0, 0.06) 100%)",
                      borderRadius: 16,
                      position: "relative",
                      boxShadow: "0 4px 15px rgba(255, 143, 0, 0.08)",
                      transition: "all 0.3s ease",
                      cursor: "pointer",
                      border: "1px solid rgba(255, 143, 0, 0.12)",
                      fontFamily: "'Rubik', sans-serif"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-6px)";
                      e.currentTarget.style.boxShadow = "0 12px 25px rgba(255, 143, 0, 0.15)";
                      e.currentTarget.style.borderColor = "rgba(255, 143, 0, 0.25)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "0 4px 15px rgba(255, 143, 0, 0.08)";
                      e.currentTarget.style.borderColor = "rgba(255, 143, 0, 0.12)";
                    }}
                  >
                    {/* Top Badge - Press Release Indicator */}
                    <div style={{
                      position: "absolute",
                      top: 16,
                      right: 16,
                      background: "#fff",
                      borderRadius: "8px",
                      padding: "6px 12px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px"
                    }}>
                      <Newspaper size={16} color="#FF8F00" strokeWidth={2} />
                      <span style={{
                        fontSize: "12px",
                        fontWeight: 500,
                        color: "#FF8F00",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px"
                      }}>Press</span>
                    </div>

                    {/* Icon Container */}


                    {/* Title */}
                    <h5 style={{
                      margin: "0 0 12px 0",
                      color: "#3C007A",
                      fontSize: "20px",
                      fontWeight: 400,
                      lineHeight: 1.3,
                      fontFamily: "'Rubik', sans-serif",
                      paddingRight: "60px",
                      marginTop: "12px"
                    }}>
                      {p.title || "Press Release"}
                    </h5>

                    {/* Description */}
                    <p style={{
                      margin: "0 0 16px 0",
                      color: "#666666",
                      fontSize: "16px",
                      fontWeight: 400,
                      lineHeight: 1.6,
                      fontFamily: "'Rubik', sans-serif",
                      minHeight: "42px"
                    }}>
                      {p.description || "Press release details and information"}
                    </p>

                    {/* Source Badge */}
                    {p.source && (
                      <div style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        padding: "6px 10px",
                        background: "rgba(60, 0, 122, 0.08)",
                        borderRadius: "6px",
                        marginBottom: "6px"
                      }}>
                        <Building2 size={14} color="#3C007A" />
                        <span style={{
                          fontSize: "14px",
                          color: "#3C007A",
                          fontWeight: 400,
                          fontFamily: "'Rubik', sans-serif"
                        }}>
                          {p.source}
                        </span>
                      </div>
                    )}

                    {/* Divider */}
                    <Divider style={{ margin: "16px 0", borderColor: "rgba(255, 143, 0, 0.15)" }} />

                    {/* Footer - Date & Download */}
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between"
                    }}>
                      {/* Date */}
                      {p.date && (
                        <div style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px"
                        }}>
                          <CalendarOutlined style={{ color: "#FF8F00", fontSize: "16px" }} />
                          <span style={{
                            fontSize: "15px",
                            color: "#333333",
                            fontWeight: 400,
                            fontFamily: "'Rubik', sans-serif"
                          }}>
                            {p.date}
                          </span>
                        </div>
                      )}

                      {/* Download Button */}
                      {p.file && (
                        <a
                          href={`${process.env.REACT_APP_API_BASE_URL}/${p.file}`}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            padding: "8px 16px",
                            background: "#FF8F00",
                            color: "#fff",
                            borderRadius: "8px",
                            fontSize: "14px",
                            fontWeight: 400,
                            textDecoration: "none",
                            transition: "all 0.2s ease",
                            boxShadow: "0 2px 8px rgba(255, 143, 0, 0.2)"
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#E67E00";
                            e.currentTarget.style.transform = "translateY(-2px)";
                            e.currentTarget.style.boxShadow = "0 4px 12px rgba(255, 143, 0, 0.3)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "#FF8F00";
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow = "0 2px 8px rgba(255, 143, 0, 0.2)";
                          }}
                        >
                          <Download size={16} />
                          <span>Download</span>
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{
                padding: "60px 20px",
                textAlign: "center",
                background: "#F8F9FA",
                borderRadius: "12px",
                border: "2px dashed #E5E7EB"
              }}>
                <div style={{
                  width: "80px",
                  height: "80px",
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, rgba(255, 143, 0, 0.1) 0%, rgba(255, 143, 0, 0.15) 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 20px auto"
                }}>
                  <Newspaper size={40} color="#FF8F00" />
                </div>
                <h3 style={{
                  color: "#333333",
                  fontSize: "20px",
                  fontWeight: 500,
                  margin: "0 0 8px 0",
                  fontFamily: "'Rubik', sans-serif"
                }}>
                  No Press Releases
                </h3>
                <p style={{
                  color: "#666666",
                  fontSize: "16px",
                  fontWeight: 400,
                  margin: 0,
                  fontFamily: "'Rubik', sans-serif"
                }}>
                  Press releases will appear here once published
                </p>
              </div>
            )}
          </div>
        );
      case 'media':
        return (
          <div style={{
            background: "#fff",
            border: "1px solid #f0f0f0",
            borderRadius: 16,
            padding: 32,
            boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
            fontFamily: "'Rubik', sans-serif"
          }}>
            {/* Header Section */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '32px' }}>
              {/* <div style={{ width: '6px', height: '28px', background: '#FF8F00', borderRadius: '4px' }} /> */}
              <h4 style={{ margin: 0, color: "#3C007A", fontSize: "24px", fontWeight: 400, fontFamily: "'Rubik', sans-serif" }}>
                Media Coverage
              </h4>

            </div>

            {Array.isArray(district.mediaCoverage) && district.mediaCoverage.length ? (
              <div style={{
                display: "grid",
                // Reduced card width by increasing column count and min-width adjustment
                gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                gap: 28
              }}>
                {district.mediaCoverage.map((m, i) => (
                  <div
                    key={i}
                    style={{
                      background: "#ffffff",
                      borderRadius: 20,
                      padding: 12,
                      display: "flex",
                      flexDirection: "column",
                      border: "1px solid #f1f1f1",
                      transition: "all 0.3s ease",
                      cursor: "pointer",
                      boxShadow: "0 2px 10px rgba(0,0,0,0.02)"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-6px)";
                      e.currentTarget.style.boxShadow = "0 12px 24px rgba(60, 0, 122, 0.08)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "0 2px 10px rgba(0,0,0,0.02)";
                    }}
                  >
                    {/* Refined Image Container with 'cover' object-fit */}
                    <div style={{
                      background: "#f9fafb",
                      borderRadius: 16,
                      height: 160,
                      overflow: "hidden",
                      marginBottom: 16,
                      border: "1px solid #f8f8f8"
                    }}>
                      <img
                        src={m.image ? `${process.env.REACT_APP_API_BASE_URL}/uploads/${m.image}` : "/api/placeholder/400/220"}
                        alt={m.title || "News Preview"}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    </div>

                    {/* News Title Section */}
                    <div style={{ padding: "0 8px" }}>
                      <h5 style={{
                        fontSize: "16px",
                        color: "#333333",
                        fontWeight: 400,
                        lineHeight: 1.5,
                        margin: "0 0 16px 0",
                        height: "72px", // Consistent height for 3 lines
                        display: "-webkit-box",
                        WebkitLineClamp: "3",
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        fontFamily: "'Rubik', sans-serif"
                      }}>
                        {m.title || "Media Coverage Summary"}
                      </h5>

                      {/* Info Bar */}
                      {/* Footer Section */}
                      <div style={{
                        marginTop: "auto",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        paddingTop: "12px",
                        borderTop: "1px solid #f0f0f0"
                      }}>
                        {/* Side-by-Side Source and Date */}
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <span style={{
                            color: "#3C007A",
                            fontSize: "14px",
                            fontWeight: 400,
                            fontFamily: "'Rubik', sans-serif",
                            background: "rgba(60, 0, 122, 0.08)",
                            padding: "4px 10px",
                            borderRadius: "6px"
                          }}>
                            {m.source || "Times of India"}
                          </span>

                          {/* Visual Separator */}
                          <span style={{ color: "#E5E7EB", fontSize: "13px" }}>•</span>

                          <span style={{
                            color: "#333",
                            fontSize: "14px",
                            fontWeight: 400,
                            fontFamily: "'Rubik', sans-serif",
                            marginLeft: "10px"
                          }}>
                            {m.date}
                          </span>
                        </div>

                        {/* Interactive Share Icon */}
                        <div style={{ display: "flex", alignItems: "center" }}>
                          {m.sourceUrl && (
                            <a
                              href={m.sourceUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: "#333333", display: "flex", alignItems: "center" }}
                            >
                              <Share2 size={18} />
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ color: "#999", textAlign: "center", padding: "60px 0" }}>
                No media coverage recorded.
              </div>
            )}
          </div>
        );
      case 'awards':
        return (
          <div style={{
            background: "#fff",
            border: "1px solid #f0f0f0",
            borderRadius: 16,
            padding: 32,
            boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
            fontFamily: "'Rubik', sans-serif"
          }}>
            {/* Section Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '60px' }}>
              {/* <div style={{ width: '6px', height: '28px', background: '#FF8F00', borderRadius: '4px' }} /> */}
              <h4 style={{ margin: 0, color: "#3C007A", fontSize: "24px", fontWeight: 400 }}>
                Awards & Recognition
              </h4>
            </div>

            {Array.isArray(district.awards) && district.awards.length ? (
              <div style={{
                display: "grid",
                // Reduced width by decreasing minmax to 280px
                gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                gap: 24
              }}>
                {district.awards.map((a, i) => (
                  <div
                    key={i}
                    style={{
                      background: "#ffffff",
                      borderRadius: 20,
                      padding: "32px 24px 24px 24px",
                      position: "relative",
                      border: "1px solid #f1f1f1",
                      boxShadow: "0 12px 40px rgba(0,0,0,0.08)",
                      transition: "all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1)", // Smoother transition
                      cursor: "pointer",
                      textAlign: "center"
                    }}
                    onMouseEnter={(e) => {
                      const container = e.currentTarget;
                      container.style.transform = "translateY(-10px)";
                      container.style.background = "#3C007A"; // Background change
                      container.style.boxShadow = "0 20px 50px rgba(60, 0, 122, 0.25)";

                      // Target text elements for color inversion
                      const h5 = container.querySelector('h5');
                      const p = container.querySelector('p');
                      const btn = container.querySelector('.cert-link');
                      if (h5) h5.style.color = "#ffffff";
                      if (p) p.style.color = "rgba(255,255,255,0.8)";
                      if (btn) btn.style.color = "#FF8F00"; // Keep amber for contrast
                    }}
                    onMouseLeave={(e) => {
                      const container = e.currentTarget;
                      container.style.transform = "translateY(0)";
                      container.style.background = "#ffffff";
                      container.style.boxShadow = "0 12px 40px rgba(0,0,0,0.08)";

                      const h5 = container.querySelector('h5');
                      const p = container.querySelector('p');
                      const btn = container.querySelector('.cert-link');
                      if (h5) h5.style.color = "#333333";
                      if (p) p.style.color = "#666666";
                      if (btn) btn.style.color = "#3C007A";
                    }}
                  >
                    {/* Achievement Badge Icon */}
                    <div style={{
                      position: "absolute",
                      top: "-35px",
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: "65px",
                      height: "65px",
                      borderRadius: "50%",
                      background: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "4px solid #fff",
                      boxShadow: "0 6px 15px rgba(255, 143, 0, 0.3)",
                      zIndex: 2
                    }}>
                      <img src="/img/trophy.png" alt="Trophy" style={{ width: "40px", height: "40px" }} />
                    </div>

                    {/* Award Content */}
                    <div style={{ marginTop: "15px" }}>
                      <span style={{
                        color: "#FF8F00",
                        fontSize: "12px",
                        fontWeight: 400,
                        textTransform: "uppercase",
                        letterSpacing: "1.2px",
                        display: "inline-block",
                        // display: "block",
                        marginBottom: "10px",
                        background: "rgba(255, 143, 0, 0.1)",
                        padding: "4px 10px",
                        borderRadius: "4px"
                      }}>
                        {a.category || "Excellence"}
                      </span>

                      <h5 style={{
                        fontSize: "17px",
                        color: "#333333",
                        fontWeight: 400,
                        lineHeight: 1.3,
                        margin: "0 0 10px 0",
                        fontFamily: "'Rubik', sans-serif",
                        transition: "color 0.3s ease"
                      }}>
                        {a.awardName || "Award Recognition"}
                      </h5>

                      <p style={{
                        fontSize: "13px",
                        color: "#666666",
                        fontWeight: 400,
                        lineHeight: 1.5,
                        margin: "0 0 18px 0",
                        minHeight: "40px",
                        transition: "color 0.3s ease"
                      }}>
                        {a.organization || "National Recognition Board"}
                      </p>

                      {/* Document Footer */}
                      <div style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        paddingTop: "14px",
                        borderTop: "2px solid rgba(0,0,0,0.05)"
                      }}>
                        {a.certificate && (
                          <a
                            className="cert-link"
                            href={`${process.env.REACT_APP_API_BASE_URL}/uploads/${a.certificate}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: "#3C007A",
                              fontSize: "13px",
                              fontWeight: 500,
                              textDecoration: "none",
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              transition: "color 0.3s ease"
                            }}
                          >
                            VIEW CERTIFICATE <FileText size={14} />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ color: "#999", textAlign: "center", padding: "60px 0" }}>
                No awards or recognitions listed for this district.
              </div>
            )}
          </div>
        );
      case 'downloads':
        return (
          <div style={{
            background: "#fdfdfd",
            borderRadius: 16,
            padding: 32,
            fontFamily: "'Rubik', sans-serif"
          }}>
            {/* Professional Section Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '40px' }}>
              {/* <div style={{ width: '6px', height: '30px', background: '#FF8F00', borderRadius: '4px' }} /> */}
              <h4 style={{ margin: 0, color: "#3C007A", fontSize: "24px", fontWeight: 400 }}>
                Resources & Downloads
              </h4>
            </div>

            {Array.isArray(district.resources) && district.resources.length ? (
              <div style={{
                display: "grid",
                // Refined grid for professional spacing
                gridTemplateColumns: "repeat(auto-fill, minmax(270px, 1fr))",
                gap: 24
              }}>
                {district.resources.map((r, i) => (
                  <div
                    key={i}
                    style={{
                      background: "#FEF9F2", // Professional cream background
                      borderRadius: 16,
                      padding: "32px 24px",
                      border: "1px solid rgba(0,0,0,0.03)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                      position: "relative",
                      transition: "all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1)",
                      boxShadow: "0 4px 15px rgba(0,0,0,0.04)"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-8px)";
                      e.currentTarget.style.boxShadow = "0 12px 30px rgba(0,0,0,0.1)";
                      e.currentTarget.style.background = "#ffffff";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "0 4px 15px rgba(0,0,0,0.04)";
                      e.currentTarget.style.background = "#FEF9F2";
                    }}
                  >

                    <div style={{
                      width: 70,
                      height: 70,
                      borderRadius: "50%",
                      background: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: 24,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.05)"
                    }}>
                      <img src="/img/download-file.png" alt="Download" style={{ width: "45px", height: "45px" }} />
                    </div>

                    <div style={{ flex: 1 }}>
                      <h5 style={{
                        fontSize: "19px",
                        color: "#000",
                        fontWeight: 400,
                        lineHeight: 1.3,
                        margin: "0 0 12px 0",
                        fontFamily: "'Rubik', sans-serif"
                      }}>
                        {r.name || "Resource Document"}
                      </h5>

                      <p style={{
                        fontSize: "15px",
                        color: "#666666",
                        fontWeight: 400, // Strictly weight 400
                        lineHeight: 1.6,
                        margin: 0
                      }}>
                        {r.description || "Access official guidelines and technical reference material."}
                      </p>
                    </div>

                  </div>
                ))}
              </div>
            ) : (
              <div style={{
                padding: "80px 0",
                textAlign: "center",
                background: "#fff",
                borderRadius: 16,
                border: "2px dashed #f0f0f0"
              }}>
                <Info size={40} color="#ccc" style={{ marginBottom: 16 }} />
                <div style={{ color: "#999", fontSize: "18px", fontWeight: 400 }}>
                  No technical resources are currently available for this district.
                </div>
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading district data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-2">Error</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!district) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-600">District not found</p>
      </div>
    );
  }

  // Sample industries data - replace with actual data from district object when available
  const industries = district.industries || [];

  return (
    <div style={{ fontFamily: "Rubik, sans-serif", backgroundColor: "#fff", marginTop: "12px" }}>

      {/* Breadcrumb */}
      <div className="district-breadcrumb" style={{
        maxWidth: "1400px",
        margin: "8px auto",
        padding: "0 20px",
        fontSize: "16px",
        color: "#666",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center"
      }}>
        <div>
          <span style={{ fontSize: "16px" }}>Districts</span>
          <span style={{ margin: "0 10px" }}>/</span>
          <span style={{ color: "#333", fontWeight: "500", fontSize: "16px" }}>{district.districtName}</span>
        </div>
      </div>

      {/* Main Section */}
      <style>{`
        .district-mobile-toggler-bar { display: none; }
        .district-mobile-toggler { display: none; }
        .district-sidebar-backdrop { display: none; }

        @media (max-width: 900px) {
          .district-mobile-toggler-bar {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            margin: 0 20px 16px;
            padding: 10px 14px;
            background: #fff;
            border: 1px solid #e5e7eb;
            border-radius: 10px;
            box-shadow: 0 1px 4px rgba(0,0,0,0.06);
          }
          .district-mobile-toggler-active {
            display: flex;
            align-items: center;
            gap: 10px;
            color: #470172;
            font-size: 16px;
            font-weight: 500;
            font-family: 'Rubik', sans-serif;
          }
          .district-mobile-toggler {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 40px;
            height: 40px;
            flex-shrink: 0;
            background: #470172;
            color: #fff;
            border: none;
            border-radius: 8px;
            cursor: pointer;
          }
          .district-content-row { display: block !important; }
          .district-sidebar-col {
            position: static !important;
            width: auto !important;
            max-width: 100%;
            box-sizing: border-box;
            margin: 0 20px 16px;
            display: none;
            border: 1px solid #e5e7eb;
            border-radius: 10px;
            box-shadow: 0 1px 4px rgba(0,0,0,0.06);
            overflow: hidden;
          }
          .district-sidebar-col.district-sidebar-open {
            display: block;
          }
          .district-sidebar-col > div {
            min-height: 0 !important;
            border-right: none !important;
            background: #fff !important;
          }
          .district-sidebar-backdrop {
            display: none !important;
          }
          .district-main-col {
            padding: 0 4px !important;
          }
          .district-breadcrumb {
            padding: 0 !important;
            justify-content: center !important;
            margin-top: 0 !important;
            margin-bottom: 20px !important;
            font-size: 19px !important;
          }
          .district-breadcrumb span {
            font-size: 19px !important;
          }
          .district-main-section {
            padding: 0 !important;
          }
        }
      `}</style>
      <section className="district-main-section" style={{
        maxWidth: "1400px",
        margin: "0 auto",
        padding: "0 20px",
        marginBottom: "60px"
      }}>
        <div
          className="district-mobile-toggler-bar"
          style={mobileSidebarOpen ? { display: "none" } : undefined}
        >
          <span className="district-mobile-toggler-active">
            {sidebarItems.find((item) => item.key === activeSection)?.icon}
            {sidebarItems.find((item) => item.key === activeSection)?.label}
          </span>
          <button
            type="button"
            className="district-mobile-toggler"
            onClick={() => setMobileSidebarOpen(true)}
            aria-label="Open sections menu"
          >
            <Menu size={22} />
          </button>
        </div>

        <div
          className="district-sidebar-backdrop"
          onClick={() => setMobileSidebarOpen(false)}
        />

        <div className="district-content-row" style={{ display: "flex", gap: "0" }}>
          {/* Sidebar (tabs) */}
          <div className={`district-sidebar-col ${mobileSidebarOpen ? "district-sidebar-open" : ""}`} style={{ width: "300px", flexShrink: 0 }}>
            <div style={{ backgroundColor: "#f8f9fa", minHeight: "400px", borderRight: "1px solid #e0e0e0" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px", borderBottom: "1px solid #e0e0e0", cursor: "pointer" }}>
                <Home size={22} color="#666" />
                <button
                  type="button"
                  className="district-mobile-toggler"
                  style={{ display: mobileSidebarOpen ? "flex" : "none", margin: 0, padding: "6px", background: "#6b2d87" }}
                  onClick={() => setMobileSidebarOpen(false)}
                  aria-label="Close sections menu"
                >
                  <X size={22} />
                </button>
              </div>
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {sidebarItems.map(item => (
                  <li
                    key={item.key}
                    style={{
                      padding: "16px 20px",
                      cursor: "pointer",
                      borderBottom: "1px solid #e0e0e0",
                      fontSize: "16px",
                      color: activeSection === item.key ? "#6b2d87" : "#666",
                      backgroundColor: activeSection === item.key ? "#f3e8fb" : "transparent",
                      borderLeft: activeSection === item.key ? "3px solid #6b2d87" : "3px solid transparent",
                      fontWeight: activeSection === item.key ? 600 : 400
                    }}
                    onClick={(e) => { e.preventDefault(); setActiveSection(item.key); setMobileSidebarOpen(false); }}
                  >
                    <a
                      href={`#${item.key}`}
                      style={{
                        textDecoration: "none",
                        color: activeSection === item.key ? "#6b2d87" : "#666",
                        display: "flex",
                        alignItems: "center",
                        fontFamily: "Rubik, sans-serif"
                      }}
                    >
                      <span style={{ marginRight: "12px", flexShrink: 0, display: "flex" }}>{item.icon}</span>
                      <span style={{ lineHeight: 1.3 }}>{item.label}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Main Content (active section content) */}
          <div className="district-main-col" style={{ flex: 1, padding: "0 40px" }}>
            {/* Header + Cover */}
            <div>
              <h3 style={{ fontWeight: 600, marginBottom: "18px", color: "#333" }}>
                {district.districtName}
              </h3>
              <div style={{ width: "100%", height: "280px", marginBottom: "22px", overflow: "hidden", borderRadius: "8px", boxShadow: "0 2px 10px rgba(0,0,0,0.06)" }}>
                <img
                  src={district.coverImage ? `${process.env.REACT_APP_API_BASE_URL}/uploads/${district.coverImage}` : "/img/company/bg.jpg"}
                  alt={district.districtName}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  onError={(e) => { e.target.src = "/img/company/bg.jpg"; }}
                />
              </div>
            </div>

            {/* Active section renderer */}
            {renderSection()}
          </div>
        </div>
      </section>

      <section style={{
        backgroundColor: "#f8f9fa",
        padding: "50px 0",
        fontFamily: "'Rubik', sans-serif"
      }}>
        <div style={{
          maxWidth: "1450px",
          margin: "0 auto",
          padding: "0 20px"
        }}>
          {/* Section Heading with Slider Controls */}
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "45px"
          }}>
            <h2 style={{
              fontSize: "28px",
              fontWeight: 500,
              margin: 0,
              color: "#333333",
              fontFamily: "'Rubik', sans-serif"
            }}>
              Featured Industries
            </h2>

            {/* Slider Controls - Only show if companies exist */}
            {companies.length > 0 && (
              <div style={{ display: "flex", gap: "12px" }}>
                <button
                  onClick={() => {
                    const container = document.getElementById('companies-container');
                    if (container) {
                      container.scrollBy({ left: -400, behavior: 'smooth' });
                    }
                  }}
                  style={{
                    width: "44px",
                    height: "44px",
                    borderRadius: "50%",
                    border: "2px solid #E5E7EB",
                    background: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "#3C007A";
                    e.currentTarget.style.background = "#F3E8FF";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#E5E7EB";
                    e.currentTarget.style.background = "#fff";
                  }}
                >
                  <ChevronLeft size={20} color="#3C007A" />
                </button>

                <button
                  onClick={() => {
                    const container = document.getElementById('companies-container');
                    if (container) {
                      container.scrollBy({ left: 400, behavior: 'smooth' });
                    }
                  }}
                  style={{
                    width: "44px",
                    height: "44px",
                    borderRadius: "50%",
                    border: "2px solid #E5E7EB",
                    background: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "#3C007A";
                    e.currentTarget.style.background = "#F3E8FF";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#E5E7EB";
                    e.currentTarget.style.background = "#fff";
                  }}
                >
                  <ChevronRight size={20} color="#3C007A" />
                </button>
              </div>
            )}
          </div>

          {/* Loading State */}
          {companiesLoading ? (
            <div style={{
              textAlign: "center",
              padding: "80px 20px",
              backgroundColor: "white",
              borderRadius: "20px",
              border: "2px dashed #E5E7EB"
            }}>
              <div style={{
                width: "60px",
                height: "60px",
                border: "4px solid #f3f4f6",
                borderTopColor: "#3C007A",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
                margin: "0 auto 20px"
              }} />
              <p style={{
                color: "#666666",
                fontSize: "17px",
                fontWeight: 400,
                fontFamily: "'Rubik', sans-serif"
              }}>
                Loading companies...
              </p>
            </div>
          ) : companiesError ? (
            /* Error State */
            <div style={{
              textAlign: "center",
              padding: "80px 20px",
              backgroundColor: "white",
              borderRadius: "20px",
              border: "2px dashed #E5E7EB"
            }}>
              <div style={{
                width: "80px",
                height: "80px",
                borderRadius: "50%",
                background: "#FEE2E2",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 20px"
              }}>
                <AlertCircle size={40} color="#DC2626" />
              </div>
              <h3 style={{
                color: "#333333",
                fontSize: "24px",
                fontWeight: 600,
                margin: "0 0 12px 0",
                fontFamily: "'Rubik', sans-serif"
              }}>
                Error Loading Companies
              </h3>
              <p style={{
                color: "#666666",
                fontSize: "16px",
                margin: 0,
                fontFamily: "'Rubik', sans-serif"
              }}>
                {companiesError}
              </p>
            </div>
          ) : companies.length === 0 ? (
            /* Empty State - No Companies */
            <div style={{
              textAlign: "center",
              padding: "80px 20px",
              backgroundColor: "white",
              borderRadius: "20px",
              border: "2px dashed #E5E7EB"
            }}>
              <div
                style={{
                  width: "100px",
                  height: "100px",
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #F3E8FF 0%, #FFF5E6 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 24px auto"
                }}
              >
                <ApartmentOutlined
                  style={{
                    fontSize: "40px",   // increase icon size here
                    color: "#3C007A"
                  }}
                />
              </div>

              <h3 style={{
                color: "#333333",
                fontSize: "24px",
                fontWeight: 500,
                margin: "0 0 12px 0",
                fontFamily: "'Rubik', sans-serif"
              }}>
                No Industries Found
              </h3>
              <p style={{
                color: "#666666",
                fontSize: "16px",
                margin: 0,
                fontFamily: "'Rubik', sans-serif"
              }}>
                Companies in {district?.districtName} will appear here once registered
              </p>
            </div>
          ) : (
            /* Companies Grid with Horizontal Scroll */
            <div
              id="companies-container"
              style={{
                display: "flex",
                gap: "24px",
                overflowX: "auto",
                overflowY: "hidden",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                WebkitOverflowScrolling: "touch",
                paddingBottom: "20px"
              }}
            >
              <style>{`
                #companies-container::-webkit-scrollbar {
                  display: none;
                }
              `}</style>

              {companies.map((company, index) => (
                <div key={company._id || index} style={{
                  backgroundColor: "white",
                  borderRadius: "12px",
                  overflow: "visible",
                  boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  cursor: "pointer",
                  position: "relative",
                  border: "1px solid #eee",
                  minWidth: "330px",
                  maxWidth: "330px",
                  flexShrink: 0
                }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-12px)";
                    e.currentTarget.style.boxShadow = "0 20px 50px rgba(60, 0, 122, 0.15)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 10px 30px rgba(0,0,0,0.08)";
                    e.currentTarget.style.borderColor = "#eee";
                  }}
                >
                  {/* Main Cover Image */}
                  <div style={{
                    height: "220px",
                    overflow: "hidden",
                    borderRadius: "12px 12px 0 0",
                    backgroundColor: "#f3f4f6"
                  }}>
                    <img
                      alt={company.companyName || "Company Banner"}
                      src={company.banner
                        ? `${process.env.REACT_APP_API_BASE_URL}/${company.banner.replace(/\\/g, '/').replace(/^\/+/, '')}`
                        : "/img/company/bg.jpg"
                      }
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        transition: "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
                      }}
                      onError={(e) => {
                        e.target.src = "/img/company/bg.jpg";
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "scale(1.05)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "scale(1)";
                      }}
                    />
                  </div>

                  {/* Floating Logo Circle */}
                  <div style={{
                    width: "100px",
                    height: "100px",
                    backgroundColor: "white",
                    borderRadius: "50%",
                    position: "absolute",
                    top: "160px",
                    left: "20px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    zIndex: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "3px solid white"
                  }}>
                    <img
                      alt={company.companyName || "Company Logo"}
                      src={company.logo
                        ? `${process.env.REACT_APP_API_BASE_URL}/${company.logo.replace(/\\/g, '/').replace(/^\/+/, '')}`
                        : "/img/company/logo-placeholder.png"
                      }
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        borderRadius: "50%"
                      }}

                    />
                  </div>

                  {/* Content Body */}
                  <div style={{ padding: "40px 20px 20px 20px" }}>
                    <h4 style={{
                      fontSize: "21px",
                      fontWeight: 400,
                      marginBottom: "6px",
                      color: "#3C007A",
                      fontFamily: "'Rubik', sans-serif"
                    }}>
                      {company.companyName}
                    </h4>

                    <p style={{
                      fontSize: "16px",
                      color: "#666666",
                      marginBottom: "20px",
                      fontWeight: "400"
                    }}>
                      {company.address?.city}
                    </p>

                    {/* Information List */}
                    <div style={{ paddingTop: "10px", marginBottom: "25px" }}>
                      <div style={{ display: "flex", marginBottom: "12px", alignItems: "center" }}>
                        <span style={{ minWidth: "100px", color: "#333333", fontSize: "16px", marginRight: "10px" }}>Industry</span>
                        <span style={{ color: "#666666", fontSize: "15px" }}>{company.industry || "N/A"}</span>
                      </div>

                      <div style={{ display: "flex", marginBottom: "12px", alignItems: "center" }}>
                        <span style={{ minWidth: "100px", color: "#333333", fontSize: "16px", marginRight: "10px" }}>Phone No.</span>
                        <span style={{ color: "#666666", fontSize: "15px" }}>{company.phone || "N/A"}</span>
                      </div>

                      <div style={{ display: "flex", alignItems: "center" }}>
                        <span style={{ minWidth: "100px", color: "#333333", fontSize: "16px", marginRight: "10px" }}>Email</span>
                        <span style={{ color: "#666666", fontSize: "15px", wordBreak: "break-word" }}>{company.email || "N/A"}</span>
                      </div>
                    </div>

                    {/* Divider */}
                    <div style={{
                      height: "1px",
                      background: "linear-gradient(90deg, transparent 0%, #e5e7eb 50%, transparent 100%)",
                      marginBottom: "20px"
                    }} />

                    {/* Actions */}
                    <div style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "5px"
                    }}>
                      <div style={{ display: "flex", gap: "7px" }}>
                        <a
                          href={`tel:${company.phone}`}
                          style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "50%",
                            backgroundColor: "rgba(255, 143, 0, 0.1)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "all 0.3s ease",
                            textDecoration: "none"
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "rgba(255, 143, 0, 0.2)";
                            e.currentTarget.style.transform = "translateY(-2px)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "rgba(255, 143, 0, 0.1)";
                            e.currentTarget.style.transform = "translateY(0)";
                          }}
                        >
                          <Phone size={20} color="#FF8F00" />
                        </a>

                        <a
                          href={`mailto:${company.email}`}
                          style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "50%",
                            backgroundColor: "rgba(255, 143, 0, 0.1)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "all 0.3s ease",
                            textDecoration: "none"
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "rgba(255, 143, 0, 0.2)";
                            e.currentTarget.style.transform = "translateY(-2px)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "rgba(255, 143, 0, 0.1)";
                            e.currentTarget.style.transform = "translateY(0)";
                          }}
                        >
                          <Mail size={20} color="#FF8F00" />
                        </a>

                        <a
                          href={company.website}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "50%",
                            backgroundColor: "rgba(255, 143, 0, 0.1)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "all 0.3s ease",
                            textDecoration: "none"
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "rgba(255, 143, 0, 0.2)";
                            e.currentTarget.style.transform = "translateY(-2px)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "rgba(255, 143, 0, 0.1)";
                            e.currentTarget.style.transform = "translateY(0)";
                          }}
                        >
                          <LinkIcon size={20} color="#FF8F00" />
                        </a>
                      </div>

                      <a
                        href={`/company/${company._id}`}
                        style={{
                          padding: "6px 28px",
                          border: "1px solid #FF8F00",
                          borderRadius: "8px",
                          color: "#FF8F00",
                          textDecoration: "none",
                          fontSize: "14px",
                          fontWeight: "400",
                          transition: "all 0.3s ease"
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#FF8F00";
                          e.currentTarget.style.color = "white";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "transparent";
                          e.currentTarget.style.color = "#FF8F00";
                        }}
                      >
                        Details
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Add keyframes for spinner animation */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default DistrictDetails;



